var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { z } from 'zod';
import { objectValues } from 'utils/TypescriptUtils';
// SCO-818: these are PostHog flag keys (LaunchDarkly is retired). A flag that
// does not exist in PostHog, or has not loaded yet, reads false.
// `scrolling_scoreboard` (on for everyone) is hard-coded on and `test` is
// deleted; neither is a flag any more.
// A flag is read once per page load: flipping one off is a rollback that
// reaches each open page when it next loads, not at once.
export var FLAGS = {
    // SCO-359: route goal saves to the Node /api/v1/goals endpoint instead of
    // legacy PHP /rest_goals. Default-off → PHP; flip on per environment after
    // verifying Node; flipping off is the rollback (at the next page load).
    GOAL_WRITE_NODE: 'goal_write_node',
    // SCO-384: show the coach one-tap "Nudge" button on the per-producer card.
    // Default-off → ships dark until the SCO-383 Node route is deployed + the
    // Virtual Coach cohort is live; flipping off hides the button (next page load).
    NUDGE_BUTTON: 'nudge_button',
    // SCO-392: render the player area inside the unified coach shell (SidebarProvider
    // + full-width Header + role-driven rail) instead of the legacy player layout.
    // Default-off → players stay on the current layout; flip on per-cohort only once
    // the parity gate (Score/Settings/real-time) is verified. Rollback: flip off
    // (next page load).
    UNIFIED_PLAYER_SHELL: 'unified_player_shell',
    // Producer Readiness Panel (locked 2026-07-02): coach-area view of the
    // per-person enablement funnel (GET /api/v1/coach/readiness). Default-off →
    // nav item + route ship dark until the backend endpoint is deployed;
    // flipping off hides the surface (next page load).
    READINESS_PANEL: 'readiness_panel',
    // SCO-552/SCO-559: route roster writes (add player, add/remove team member)
    // to the Node endpoints instead of legacy PHP /rest_users and
    // /rest_teams/{add,remove}_user. Default-off → PHP; flip on per environment
    // once the Node routes are deployed. Flipping off is the rollback (next page
    // load).
    // NOTE: while this is off, the PHP paths are still live, and only add_user
    // has its ownership ladder restored (scoreboard#152) — remove_user stays
    // unguarded until this flag is on everywhere and the PHP methods are deleted.
    ROSTER_WRITE_NODE: 'roster_write_node',
    // SCO-187: the HubSpot integration surface (the card on Integrations and
    // the /integrations/hubspot settings page). Default-off → ships dark: the
    // backend holds Connect and backfill at 503 until SCO-760, and Nango has no
    // HubSpot integration configured, so the card could only fail. Flip on per
    // environment once Connect works there; flipping off hides both together
    // (next page load).
    HUBSPOT_INTEGRATION: 'hubspot_integration',
    // SCO-583 (producer onboarding, slice 3): the producer's setup pop-down.
    // The SAME account-group flag the backend reads to decide whether an
    // office is in onboarding, so the page asks the setup endpoint only where
    // the backend would answer something. Default-off, and the flag does not
    // exist in PostHog yet → reads false everywhere: no request to
    // /api/v1/me/setup and nothing rendered. A flag that has not loaded reads
    // false too. Turning it on is per office, on James's word.
    PRODUCER_ONBOARDING_ENABLED: 'producer_onboarding_enabled'
};
// The store's runtime data is keyed by the flag keys — the FLAGS *values*
// (services/featureFlags.ts builds it). The schema was previously built from
// the constant NAMES, inverting the type against the runtime data: a
// type-correct call like flag('READINESS_PANEL') compiled but always returned
// false, and every working call site needed a `FLAGS.X as 'X'` cast to smuggle
// the runtime value past the wrong type. Keying by values makes
// flag(FLAGS.READINESS_PANEL) both type-correct and runtime-correct.
var _a = objectValues(FLAGS), firstFlag = _a[0], otherFlags = _a.slice(1);
var KnownFlagSchema = z.enum(__spreadArray([firstFlag], otherFlags, true));
export var FlagsSchema = z.record(KnownFlagSchema, z.boolean());
